import * as React from "react";
import { Check } from "lucide-react";

import { cn } from "../../lib/utils";
import { AppLayout } from "../../components/ui-v2/layout/AppLayout";
import {
  Card,
  CardHeader,
  CardContent,
  CardDescription,
  CardTitle,
} from "../../components/ui-v2/card";
import { Badge } from "../../components/ui-v2/badge";
import { useTheme } from "../../context/ThemeContext";
import axios from "axios";
import { API_BASE_URL } from "../../lib/api";
import { useAuth } from "../../AuthContext";
import { McpApiKeySection } from "../../components/ui-v2/McpApiKeySection";
import { ConnectedAppsSection } from "../../components/ui-v2/ConnectedAppsSection";

/**
 * V2 Settings Page — Institutional Console redesign (issue #237,
 * DESIGN-SPEC.md §5 "Settings").
 *
 * Layout: left section rail (Appearance / Integrations / Account, sticky,
 * §5 "left section rail") + right content column (max-width 1080px, §2.4
 * pattern for narrow settings-style screens) containing:
 * - Appearance: theme cards, "Console Dark" (default) and "Console Light"
 *   first, then legacy themes ("dark"/"light" relabeled "Legacy Dark"/
 *   "Legacy Light" per Settings.dc.html), all wired to ThemeContext (#231).
 * - Integrations: MCP panel (unchanged McpApiKeySection, wrapped in the
 *   spec's card chrome) plus "Connected apps" (ConnectedAppsSection, issue
 *   #257) — OAuth clients (e.g. ChatGPT) approved on the consent page,
 *   listed with an in-page-confirmed Revoke action.
 * - Account: placeholder (no account-management functionality existed
 *   pre-redesign; kept as a stub section, not a functionality regression).
 *
 * The section rail is presentational-only (no distinct routes existed for
 * these sub-sections before this issue, and none are introduced here per
 * CC4 — clicking a rail item scrolls to its section instead of navigating).
 */

// Theme configuration — Console Dark/Light first (per Settings.dc.html,
// Console Dark is the default selection), then legacy themes retained
// verbatim (all previously-selectable theme ids kept, per "no functionality
// lost").
const themeConfigs = [
  {
    id: "console-dark",
    name: "Console Dark",
    description: "Deep blue-slate, default",
    colors: ["#0a1119", "#101a26", "#3b9eff", "#2bb596"],
  },
  {
    id: "console-light",
    name: "Console Light",
    description: "Cool paper-white companion",
    colors: ["#f4f6f8", "#ffffff", "#0e6fc4", "#0c805f"],
  },
  {
    id: "dark",
    name: "Legacy Dark",
    description: "Previous GitHub-dark theme",
    colors: ["#0d1117", "#161b22", "#58a6ff", "#238636"],
  },
  {
    id: "light",
    name: "Legacy Light",
    description: "Previous light theme",
    colors: ["#ffffff", "#f6f8fa", "#2563eb", "#16a34a"],
  },
  {
    id: "navy",
    name: "Navy",
    description: "Professional deep blue tones",
    colors: ["#0C2B4E", "#1A3D64", "#5ea9c7", "#1D546C"],
  },
  {
    id: "brownie",
    name: "Brownie",
    description: "Warm and cozy brown palette",
    colors: ["#5d4029", "#8B6F47", "#FFBB00", "#ECB176"],
  },
  {
    id: "ai-fantasy",
    name: "AI Fantasy",
    description: "Futuristic cyberpunk vibes",
    colors: ["#08091f", "#1a2045", "#00a6f0", "#a800ff"],
  },
  {
    id: "slate-finance",
    name: "Slate Finance",
    description: "Professional teal & slate for finance",
    colors: ["#f8fafc", "#ffffff", "#0d7377", "#10b981"],
  },
  {
    id: "slate-finance-dark",
    name: "Slate Finance Dark",
    description: "Professional dark mode with teal accents",
    colors: ["#020617", "#0f172a", "#2dd4bf", "#34d399"],
  },
];

const SECTIONS = [
  { id: "appearance", label: "Appearance" },
  { id: "story", label: "Landing page" },
  { id: "integrations", label: "Integrations" },
  { id: "account", label: "Account" },
];

/**
 * SectionRail - left sticky section navigation (§5 "left section rail").
 * Scrolls the corresponding section into view; no routing change (CC4).
 */
function SectionRail({ activeSection, onSelect }) {
  return (
    <nav
      className="sticky top-[24px] flex flex-col gap-0.5 text-[12.5px]"
      aria-label="Settings sections"
    >
      {SECTIONS.map((section) => {
        const isActive = section.id === activeSection;
        return (
          <button
            key={section.id}
            type="button"
            onClick={() => onSelect(section.id)}
            aria-current={isActive ? "true" : undefined}
            className={cn(
              "rounded-md px-3 py-[7px] text-left transition-colors duration-[120ms] ease-out",
              "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent,_#3b9eff)]",
              isActive
                ? "bg-accent-tint font-semibold text-accent-text"
                : "text-muted-token hover:bg-[rgba(255,255,255,0.04)]"
            )}
          >
            {section.label}
          </button>
        );
      })}
    </nav>
  );
}

// Swatch color for a theme card (§5 mockup: 18x18, r4, thin translucent border)
function ThemeSwatch({ color, label }) {
  return (
    <span
      className="block h-[18px] w-[18px] shrink-0 rounded border border-[rgba(255,255,255,0.12)]"
      style={{ backgroundColor: color }}
      title={label}
      aria-label={label}
    />
  );
}

/**
 * ThemeCard - single theme selector card (§5 Settings.dc.html: r7, p12/14,
 * accent border + tint bg when active, "Active" badge top-right of the
 * name row).
 */
function ThemeCard({ config, isActive, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex flex-col rounded-[7px] border p-3.5 text-left transition-colors duration-[120ms] ease-out",
        "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent,_#3b9eff)]",
        isActive
          ? "border-[var(--accent,_#3b9eff)] bg-accent-tint"
          : "border-border hover:border-[#2a4763]"
      )}
      aria-pressed={isActive}
      aria-label={`Select ${config.name} theme${isActive ? " (currently active)" : ""}`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-[12.5px] font-semibold text-foreground">{config.name}</span>
        {isActive && (
          <Badge variant="default" size="sm">
            <Check className="mr-1 h-2.5 w-2.5" aria-hidden="true" />
            Active
          </Badge>
        )}
      </div>
      <div className="mt-[3px] text-[11px] leading-[1.4] text-text-faint">
        {config.description}
      </div>
      <div className="mt-2.5 flex items-center gap-[5px]">
        {config.colors.map((color, index) => (
          <ThemeSwatch key={color} color={color} label={`${config.name} swatch ${index + 1}`} />
        ))}
      </div>
    </button>
  );
}

/**
 * AppearanceSection - theme picker card (§5 "theme cards incl. Console Dark
 * default / Console Light / legacy").
 */
const AppearanceSection = React.forwardRef(function AppearanceSection(_, ref) {
  const { theme, setTheme } = useTheme();

  return (
    <Card ref={ref} id="settings-appearance" className="scroll-mt-6">
      <CardHeader className="border-b border-hairline px-[18px] py-3.5">
        <CardTitle className="text-[13.5px] font-semibold">Theme</CardTitle>
        <CardDescription className="text-xs text-text-faint">
          Choose your preferred color theme. Saved automatically.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid grid-cols-1 gap-2.5 px-[18px] py-4 sm:grid-cols-2 lg:grid-cols-3">
        {themeConfigs.map((config) => (
          <ThemeCard
            key={config.id}
            config={config}
            isActive={theme === config.id}
            onClick={() => setTheme(config.id)}
          />
        ))}
      </CardContent>
    </Card>
  );
});

/**
 * LandingSection - which page opens after login (issue #250).
 *
 * The preference lives on the user profile rather than in localStorage, so it
 * follows the account between devices, and the OAuth callback resolves it
 * server-side. The option list is fetched rather than hard-coded so this UI and
 * the redirect can never disagree about what is allowed.
 */
const LandingSection = React.forwardRef(function LandingSection(_, ref) {
  const { user, setUser } = useAuth();
  const [options, setOptions] = React.useState([]);
  const [saving, setSaving] = React.useState(null);
  const [error, setError] = React.useState(null);

  const current = user?.preferences?.landingPage || "dashboard";

  React.useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API_BASE_URL}/auth/landing-pages`, { withCredentials: true })
      .then((res) => {
        if (!cancelled) setOptions(res.data.landingPages || []);
      })
      .catch(() => {
        if (!cancelled) setOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const choose = React.useCallback(
    async (id) => {
      if (id === current) return;
      setSaving(id);
      setError(null);
      try {
        const res = await axios.patch(
          `${API_BASE_URL}/auth/preferences`,
          { landingPage: id },
          { withCredentials: true }
        );
        // Reflect the saved value from the server rather than the clicked one,
        // so a rejected value never looks accepted.
        setUser((prev) =>
          prev ? { ...prev, preferences: res.data.preferences } : prev
        );
      } catch (err) {
        setError(
          err.response?.data?.message || "Could not save that. Please try again."
        );
      } finally {
        setSaving(null);
      }
    },
    [current, setUser]
  );

  return (
    <Card ref={ref} id="settings-landing" className="scroll-mt-6">
      <CardHeader className="border-b border-hairline px-[18px] py-3.5">
        <CardTitle className="text-[13.5px] font-semibold">Landing page</CardTitle>
        <CardDescription className="text-xs text-text-faint">
          Which page opens when you log in. Saved to your profile, so it follows you
          between devices.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-1.5 px-[18px] py-4">
        {options.length === 0 && (
          <div className="text-xs text-text-faint">Loading options…</div>
        )}
        {options.map((option) => {
          const isActive = option.id === current;
          return (
            <button
              key={option.id}
              type="button"
              onClick={() => choose(option.id)}
              disabled={saving !== null}
              aria-pressed={isActive}
              className={cn(
                "flex items-center justify-between rounded-[7px] border px-3.5 py-2.5 text-left transition-colors duration-[120ms] ease-out",
                "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent,_#3b9eff)]",
                isActive
                  ? "border-[var(--accent,_#3b9eff)] bg-accent-tint"
                  : "border-border hover:border-[#2a4763]"
              )}
            >
              <span className="text-[12.5px] font-semibold text-foreground">
                {option.label}
              </span>
              {isActive && (
                <Badge variant="default" size="sm">
                  <Check className="mr-1 h-2.5 w-2.5" aria-hidden="true" />
                  Opens on login
                </Badge>
              )}
              {saving === option.id && (
                <span className="text-[11px] text-text-faint">Saving…</span>
              )}
            </button>
          );
        })}
        {error && <div className="mt-1 text-xs text-destructive">{error}</div>}
      </CardContent>
    </Card>
  );
});

/**
 * IntegrationsSection - wraps the existing McpApiKeySection with the §5
 * card chrome/heading. McpApiKeySection itself is unchanged (out of scope:
 * it is not one of the five owned screens/components), functionality
 * retained 1:1. ConnectedAppsSection (issue #257) is new: OAuth clients
 * (e.g. ChatGPT's connector) are a second, separate integration path
 * alongside the MCP API key, so it gets its own card rather than being
 * merged into McpApiKeySection's.
 */
const IntegrationsSection = React.forwardRef(function IntegrationsSection(_, ref) {
  return (
    <div ref={ref} id="settings-integrations" className="scroll-mt-6 space-y-6">
      <McpApiKeySection />
      <ConnectedAppsSection />
    </div>
  );
});

/**
 * AccountSection - placeholder (§5 lists Account as a rail item; no
 * account-management functionality existed before this issue, so this
 * mirrors the pre-existing "More Settings Coming Soon" placeholder rather
 * than inventing new functionality).
 */
const AccountSection = React.forwardRef(function AccountSection(_, ref) {
  return (
    <Card ref={ref} id="settings-account" className="scroll-mt-6 border-dashed">
      <CardContent className="px-[18px] py-4">
        <div className="text-[13px] font-semibold text-muted-token">
          More settings coming soon
        </div>
        <div className="mt-0.5 text-xs text-text-faint">
          Notifications, data export and account preferences will land here.
        </div>
      </CardContent>
    </Card>
  );
});

export function Settings() {
  const [activeSection, setActiveSection] = React.useState("appearance");
  const appearanceRef = React.useRef(null);
  const storyRef = React.useRef(null);
  const integrationsRef = React.useRef(null);
  const accountRef = React.useRef(null);

  const sectionRefs = React.useMemo(
    () => ({
      appearance: appearanceRef,
      story: storyRef,
      integrations: integrationsRef,
      account: accountRef,
    }),
    []
  );

  const handleSelectSection = React.useCallback((sectionId) => {
    setActiveSection(sectionId);
    sectionRefs[sectionId]?.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [sectionRefs]);

  return (
    <AppLayout title="Settings">
      <div className="mx-auto grid max-w-[1080px] grid-cols-1 items-start gap-7 md:grid-cols-[180px_1fr]">
        <SectionRail activeSection={activeSection} onSelect={handleSelectSection} />

        <div className="flex min-w-0 flex-col gap-6">
          <AppearanceSection ref={appearanceRef} />
          <LandingSection ref={storyRef} />
          <IntegrationsSection ref={integrationsRef} />
          <AccountSection ref={accountRef} />
        </div>
      </div>
    </AppLayout>
  );
}

export default Settings;
